import { DATA_SOURCES, TIKTOK_COUNTRIES } from '@/config/constants';
import { useEntity, useTikTokDataPoints } from 'utils/hooks/api';
import bugsnagClient from 'containers/BugsnagClient';
import { parseTikTokTimeSeriesQuery } from 'utils/helpers';
import useEntityBase, { isEmptyRow } from './useEntityBase';
import useTikTokWeeks from 'utils/hooks/useTikTokWeeks';

const filtersFor = ({ entityType, entityId, country }) => ({
    [entityType]: entityId ? [entityId] : null,
    countries: country ? [country.id] : null,
});

/**
 * One TikTok entity - a brand or a category - with its week's row and its
 * whole series.
 *
 * `country` is the caller's: most pages read `useCurrentCountry`, and the
 * brand page still reads the geo reducer, which lags it by a render.
 */
const useTikTokEntity = ({
    entityType,
    entityId,
    country,
    controller,
    coverage,
}) => {
    const entityQuery = useEntity({
        id: entityId,
        entityType,
        dataSource: DATA_SOURCES.TIKTOK,
    });
    const entity = entityQuery.data;
    const { week, year, query: weeksQuery } = useTikTokWeeks();
    const filters = filtersFor({ entityType, entityId, country });

    const [rowQuery] = useTikTokDataPoints({
        enabled: !!(country && entityId && week && year),
        ...filters,
        week,
        year,
    });
    /* A second, unbounded call: the hero reads the week's point, the chart the
       whole series. It is a hook, so it cannot be conditional. */
    const [seriesQuery] = useTikTokDataPoints({
        enabled: !!(country && entityId),
        ...filters,
        week: null,
        year: null,
    });
    const row = rowQuery.data?.data?.[0];
    const details = entity || row || null;

    return useEntityBase({
        dataSource: DATA_SOURCES.TIKTOK,
        slug: 'tiktok',
        entityType,
        entityId,
        controller,
        entity,
        entityQuery,
        details,
        country,
        week,
        year,
        weeksQuery,
        row,
        rowQuery,
        series: parseTikTokTimeSeriesQuery(seriesQuery, bugsnagClient),
        seriesQuery,
        // A brand row names its brand in `brand_name`.
        name: entity?.name || row?.brand_name || row?.name,
        isLoading:
            entityQuery.isLoading ||
            rowQuery.isLoading ||
            !(country && week && year),
        isEmpty: isEmptyRow({
            rowQuery,
            row,
            volumeKey: 'non_cum_avg_weekly_views',
        }),
        coverage,
        coveredCountries: TIKTOK_COUNTRIES,
    });
};

/** The siblings the category score card compares this entity against. */
export const useTikTokSiblings = ({ parentCategory, country, week, year }) => {
    const queries = useTikTokDataPoints({
        enabled: !!(parentCategory && country && week && year),
        countries: country ? [country.id] : null,
        categories: parentCategory ? parentCategory.children : null,
        week,
        year,
    });
    const siblings = queries.reduce((acc, { isSuccess, data }) => {
        if (!(isSuccess && data && data.data)) return acc;
        if (!acc) return data.data.slice();
        acc.push(...data.data);
        return acc;
    }, null);
    return {
        siblings,
        ready: !!(siblings && queries.every(({ isSuccess }) => isSuccess)),
    };
};

export default useTikTokEntity;
